import { useEffect, useRef, useState } from 'react';
import { Loan } from '../../../../gql/graphql';
import { useGetLoansByOwner } from '../../../../services/loan/querys';
import { useInfiniteScroll } from '../../../../@common/utils/hooks/useInfiniteScroll';

interface UseLoansParams {
  lockedDeadLayout: boolean;
  setLockedDeadLayout: (value: boolean) => void;
  setHandlerVisible: (value: boolean) => void;
}

const ITEMS_PER_LOAD = 4;

const getStateLoan = (loan: Loan) => {
  if (loan.progress?.find(item => item.state === 'error')) {
    return { key: 'error', label: 'NEEDS ATTENTION' };
  }
  return { key: 'incomplete', label: 'IN PROGRESS' };
};

export const useLoans = ({ lockedDeadLayout, setLockedDeadLayout, setHandlerVisible }: UseLoansParams) => {
  // Estados de paginación y datos
  const [page, setPage] = useState(1);
  const [allLoadedLoans, setAllLoadedLoans] = useState<Loan[]>([]);

  // Estados de filtros
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [loanStateFilter, setLoanStateFilter] = useState<'Active' | 'Closed' | 'Waiting' | 'Dead' | 'All'>('Active');

  // Estado para pausar query durante reset
  const [isPaused, setIsPaused] = useState(false);

  // Prevenir incremento de page durante cambio de filtros
  const isResettingRef = useRef(false);
  const isMountedRef = useRef(false);

  const {
    data: loans,
    hasMore,
    tabTotals,
    fetching
  } = useGetLoansByOwner({
    variables: {
      search: searchTerm,
      loanState: loanStateFilter,
      page,
      limit: ITEMS_PER_LOAD,
      includeLoanDocs: true
    },
    pause: isPaused,
    requestPolicy: 'network-only'
  });

  // Acumular loans cuando llegan nuevos
  useEffect(() => {
    if (isPaused || !loans) return;

    if (loans.length > 0) {
      setAllLoadedLoans(prev => {
        if (page === 1) {
          return loans as Loan[];
        }
        const existingIds = new Set(prev.map(loan => loan._id));
        const newLoans = loans.filter(loan => !existingIds.has(loan._id));
        return [...prev, ...newLoans] as Loan[];
      });
    } else if (page === 1) {
      setAllLoadedLoans([]);
    }
  }, [loans, page, isPaused]);

  // Reset cuando cambian filtros (no en el mount inicial — la query ya se disparó sola)
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    isResettingRef.current = true;
    setAllLoadedLoans([]);
    setPage(1);
    setIsPaused(true);

    const timer = setTimeout(() => {
      setIsPaused(false);
      isResettingRef.current = false;
    }, 100);

    return () => {
      clearTimeout(timer);
      isResettingRef.current = false;
    };
  }, [loanStateFilter, searchTerm]);

  // Manejar locked dead layout
  useEffect(() => {
    if (lockedDeadLayout) {
      setLoanStateFilter(prev => prev === 'Dead' ? prev : 'Dead');
    }
  }, [lockedDeadLayout, setHandlerVisible, setLockedDeadLayout]);

  // Infinite scroll usando hook personalizado
  const sentinelRef = useInfiniteScroll({
    hasMore,
    isLoading: fetching || isPaused,
    onLoadMore: () => {
      if (!isPaused && !isResettingRef.current) {
        setPage(prev => prev + 1);
      }
    }
  });

  // Handlers
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const tabFilteredLoans = (isPaused || (fetching && page === 1)) ? [] : allLoadedLoans.filter((loan: any) => {
    if (filter === 'all') return true;
    const stateLoan = getStateLoan(loan);
    return stateLoan.key === filter;
  });

  return {
    loanStateFilter,
    setLoanStateFilter,
    searchTerm,
    handleSearch,
    tabTotals,
    filter,
    setFilter,
    tabFilteredLoans,
    fetching,
    page,
    hasMore,
    sentinelRef,
    isPaused,
  };
};
